import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Avatar, Bar, Button, Card, Chip, Countdown, EmptyState, ErrorNotice, Field, Input, Notice, PageHeader, Skeleton, Table, TabPanel, Tabs } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { money } from '@/lib/format';
import type { BR, Candidate, EV, Queue } from '@/lib/types';
import { AdminBrChip, AdminEvChip, regionIdFor, settingValue, useRegions, useSettings } from './common';

type Tab = 'all' | 'brokers' | 'awaiting' | 'evaluations';
type Row = { kind: 'BR'; id: string; number: string; title: string; district: string; createdAt: string; br: BR } | { kind: 'EV'; id: string; number: string; title: string; district: string; createdAt: string; ev: EV };

export function AdminQueue() {
  const { t, locale, localTitle } = useI18n();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('all');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const queue = useQuery({ queryKey: ['admin', 'queue'], queryFn: () => api.get<Queue>('/api/admin/queue'), refetchInterval: 30_000 });
  const regions = useRegions();
  const settings = useSettings();
  const windowMin = settingValue<number>(settings.data?.items, 'accept_window.minutes', 60);
  const selectedId = params.get('select');

  const rows = useMemo<Row[]>(() => {
    if (!queue.data) return [];
    const br: Row[] = queue.data.brokerRequests.map((r) => ({ kind: 'BR', id: r.id, number: r.number, title: localTitle(r), district: r.property?.district ?? '', createdAt: r.createdAt, br: r }));
    const ev: Row[] = queue.data.evaluations.map((e) => ({ kind: 'EV', id: e.id, number: e.number, title: localTitle(e), district: e.district, createdAt: e.createdAt, ev: e }));
    const all = [...br, ...ev];
    if (tab === 'brokers') return br.filter((r) => r.kind === 'BR' && r.br.status === 'new');
    if (tab === 'awaiting') return all.filter((r) => (r.kind === 'BR' ? r.br.status === 'assigned' : r.ev.status === 'assigned'));
    if (tab === 'evaluations') return ev;
    return all;
  }, [queue.data, tab, localTitle]);
  const selected = rows.find((r) => r.id === selectedId) ?? (queue.data ? [...queue.data.brokerRequests.map((r) => ({ kind: 'BR' as const, id: r.id, number: r.number, title: localTitle(r), district: r.property?.district ?? '', createdAt: r.createdAt, br: r })), ...queue.data.evaluations.map((e) => ({ kind: 'EV' as const, id: e.id, number: e.number, title: localTitle(e), district: e.district, createdAt: e.createdAt, ev: e }))].find((r) => r.id === selectedId) : undefined);
  // Seeded from the note already on the request. Assigning writes this field, so opening the panel on
  // a request that carries one and assigning again used to replace it with nothing.
  useEffect(() => setNote(selected?.kind === 'BR' ? (selected.br.adminNotes ?? '') : ''), [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const regionId = selected ? regionIdFor(regions.data?.items, selected.district) : undefined;
  const candidates = useQuery({
    queryKey: ['admin', 'candidates', selected?.kind, regionId ?? ''],
    queryFn: () => api.get<{ items: Candidate[] }>(`/api/admin/${selected!.kind === 'BR' ? 'broker' : 'evaluator'}-candidates${regionId ? `?regionId=${regionId}` : ''}`),
    enabled: !!selected,
  });
  useEffect(() => {
    setPicked(null);
    setNote('');
  }, [selectedId]);
  const assign = useMutation({
    mutationFn: async () => {
      if (!selected || !picked) return;
      if (selected.kind === 'BR') await api.post(`/api/admin/broker-requests/${selected.id}/assign`, { brokerUserId: picked, note: note.trim() || undefined });
      else await api.post(`/api/admin/evaluations/${selected.id}/assign`, { evaluatorUserId: picked });
    },
    onSuccess: () => {
      setDone(selected?.number ?? null);
      void qc.invalidateQueries({ queryKey: ['admin'] });
      setParams({});
    },
  });

  if (queue.isLoading) return <Skeleton lines={8} />;
  if (queue.error) return <ErrorNotice error={queue.error} retry={() => void queue.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const q = queue.data!;
  const counts = { all: q.brokerRequests.length + q.evaluations.length, brokers: q.brokerRequests.filter((r) => r.status === 'new').length, awaiting: q.brokerRequests.filter((r) => r.status === 'assigned').length + q.evaluations.filter((e) => e.status === 'assigned').length, evaluations: q.evaluations.length };
  const roleWord = selected?.kind === 'EV' ? t('app.evaluator') : t('app.broker');
  const pickedCandidate = candidates.data?.items.find((c) => c.id === picked);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.queue') }]} title={t('nav.queue')} />
      {done ? <Notice tone="success">{t('admin.queue.assigned')} · <span className="num">{done}</span></Notice> : null}
      <Tabs<Tab> id="admin-queue" value={tab} onChange={setTab} items={[{ key: 'all', label: t('admin.queue.tabs.all'), count: counts.all }, { key: 'brokers', label: t('admin.queue.tabs.brokers'), count: counts.brokers }, { key: 'awaiting', label: t('admin.queue.tabs.awaiting'), count: counts.awaiting }, { key: 'evaluations', label: t('admin.queue.tabs.evaluations'), count: counts.evaluations }]} />
      <TabPanel id="admin-queue" value={tab}>
        <div className="grid min-[1360px]:grid-cols-[minmax(0,1.7fr)_minmax(320px,1fr)] gap-6 items-start">
          <Card className="p-0 overflow-hidden">
            {rows.length ? (
              <Table className="border-0 rounded-none">
                <thead><tr><th className="w-px">{t('app.number')}</th><th>{t('app.property')}</th><th className="w-px">{t('app.status')}</th><th className="w-px"></th></tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const assigned = r.kind === 'BR' ? r.br.status === 'assigned' : r.ev.status === 'assigned';
                    const deadline = r.kind === 'BR' ? r.br.acceptDeadlineAt : r.ev.acceptDeadlineAt;
                    const isSel = r.id === selectedId;
                    return (
                      <tr key={r.id} className={isSel ? 'bg-bg-2' : ''}>
                        <td className="num">{r.kind === 'BR' ? <Link to={`/admin/broker-requests/${r.id}`}>{r.number}</Link> : <Link to={`/admin/evaluations/${r.id}`}>{r.number}</Link>}</td>
                        {/* The floor only matters when the table is squeezed; at 180 px the row's min-content
                            (number + property + a running accept countdown + the widest action button) was 3 px
                            short of the card in English at 1440, so a longer chip tipped it into a scroll. */}
                        <td className="min-w-[140px]">
                          <div>{localTitle(r)}</div>
                          <div className="text-muted text-[13px] mt-0.5">{r.district ? <span><bdi>{r.district}</bdi> · </span> : null}<span className="whitespace-nowrap">{formatRelative(r.createdAt, locale)}</span></div>
                        </td>
                        <td className="whitespace-nowrap">
                          {assigned && deadline ? (
                            <Chip form="outlined">
                              {t(r.kind === 'BR' ? 'status.awaitingAssignment' : 'status.awaitingAssignment') && (r.kind === 'BR' ? t('dashboard.acceptWithin') : t('dashboard.acceptWithin'))} <Countdown deadline={deadline} expiredLabel={t('dashboard.expired')} />
                            </Chip>
                          ) : r.kind === 'BR' ? (
                            <AdminBrChip br={r.br} />
                          ) : (
                            <AdminEvChip ev={r.ev} />
                          )}
                        </td>
                        <td className="text-end whitespace-nowrap">
                          <Button variant={isSel ? 'accent' : assigned ? 'text' : 'secondary'} className="min-h-9 px-3 text-[14px] whitespace-nowrap" onClick={() => setParams({ select: r.id })}>
                            {assigned ? t('admin.queue.reassign') : t(r.kind === 'BR' ? 'admin.queue.assignBroker' : 'admin.queue.assignEvaluator')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="p-5"><EmptyState title={t('dashboard.allClear')} /></div>
            )}
          </Card>
          <aside className="rounded-sm border border-line-2 bg-bg p-6 min-[1360px]:sticky min-[1360px]:top-6 flex flex-col gap-5">
            {!selected ? (
              <p className="text-muted text-[14px]">{t('admin.queue.pickFirst')}</p>
            ) : (
              <>
                <div>
                  <p className="label">{t((selected.kind === 'BR' ? selected.br.status : selected.ev.status) === 'assigned' ? 'admin.queue.reassignFor' : 'admin.queue.assignFor', { role: roleWord })}</p>
                  <div className="flex items-center justify-between gap-3 mt-1">
                    <h2>{localTitle(selected)}</h2>
                    <span className="num text-muted text-[13px]">{selected.number}</span>
                  </div>
                  {selected.kind === 'BR' && selected.br.property ? (
                    <p className="text-[14px] text-fg-2 mt-1.5">
                      <span className="num">{selected.br.property.areaM2} {t('app.m2')}</span>
                      {selected.br.property.bedrooms != null ? <span> · <span className="num">{selected.br.property.bedrooms}</span> {locale === 'ar' ? 'غرف' : 'beds'}</span> : null}
                      {selected.br.property.ownerEstimateHalalas != null ? <span> · {t('dashboard.ownerEstimate')} <span className="num">{money(selected.br.property.ownerEstimateHalalas, locale)}</span></span> : null}
                    </p>
                  ) : null}
                  {selected.kind === 'EV' ? <p className="text-[14px] text-fg-2 mt-1.5"><span className="num">{selected.ev.areaM2} {t('app.m2')}</span>{selected.ev.notes ? <span className="text-muted"> · «{selected.ev.notes}»</span> : null}</p> : null}
                  <p className="text-[13px] text-muted mt-1">{t('app.owner')}: {selected.kind === 'BR' ? selected.br.ownerName : selected.ev.ownerName}</p>
                </div>
                <div>
                  <p className="label mb-2">{t('admin.queue.candidates')}</p>
                  {candidates.isLoading ? <Skeleton lines={3} /> : null}
                  {candidates.data && !candidates.data.items.length ? <p className="text-muted text-[14px]">{t('admin.queue.noCandidates', { role: roleWord })}</p> : null}
                  <div className="flex flex-col gap-2" role="radiogroup">
                    {candidates.data?.items.map((c) => {
                      const current = selected.kind === 'BR' ? selected.br.brokerUserId === c.id : selected.ev.evaluatorUserId === c.id;
                      const full = c.slots.free < 1 || current;
                      const sel = picked === c.id;
                      return (
                        <label key={c.id} className={`flex items-center gap-3 px-3.5 py-3 rounded-sm border cursor-pointer ${sel ? 'border-accent bg-bg-2' : 'border-line'} ${full ? 'opacity-55' : ''}`}>
                          <input type="radio" name="candidate" className="sr-only" checked={sel} disabled={full} onChange={() => setPicked(c.id)} />
                          <span className={`size-4 rounded-full border flex-none ${sel ? 'border-[5px] border-accent' : 'border-input-line'}`} aria-hidden />
                          <Avatar name={c.name} size={36} />
                          <span className="flex-1 min-w-0">
                            <b className="font-medium">{c.name}</b>
                            {c.officeName ? <span> · {c.officeName}</span> : null}
                            <br />
                            <span className="text-muted text-[13px]">{[c.region, c.slots.tier ? (locale === 'ar' ? c.slots.tier.nameAr : c.slots.tier.nameEn) : null, current ? t('admin.queue.current') : full ? t('admin.queue.full') : null].filter(Boolean).join(' · ')}</span>
                          </span>
                          <span className="w-[110px] flex-none">
                            <span className="num text-[13px]">{c.slots.used} / {c.slots.max} {t('app.slots')}</span>
                            <Bar value={c.slots.used} max={c.slots.max} className="mt-1" />
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                {selected.kind === 'BR' ? (
                  <Field label={t('admin.queue.noteForStaff')} htmlFor="assign-note">
                    <Input id="assign-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('admin.queue.notePlaceholder')} />
                  </Field>
                ) : null}
                {assign.error ? <Notice tone="error">{errorText(assign.error, t)}</Notice> : null}
                <Button variant="primary" className="w-full" disabled={!picked} loading={assign.isPending} onClick={() => assign.mutate()}>
                  {pickedCandidate ? t('admin.queue.assignNamed', { name: pickedCandidate.name }) : t(selected.kind === 'BR' ? 'admin.queue.assignBroker' : 'admin.queue.assignEvaluator')}
                </Button>
                <p className="text-muted text-[13px]">{t('admin.queue.assignHint', { role: roleWord, n: windowMin })}</p>
              </>
            )}
          </aside>
        </div>
      </TabPanel>
    </div>
  );
}
